<!-- ParcProfileParcCardRenouvellementListTable.vue -->
<template>
  <b-table-simple
    :bordered="!isEditable"
    :hover="isEditable"
    small
    caption-top
    responsive
    v-if="renouvellements && renouvellements.length > 0"
    class="pt-3"
    id="table-list-renouvellement"
  >
    <b-thead variant="dark">
      <b-tr>
        <b-th>Type</b-th>
        <b-th v-if="isSubstantiel">Dépôt du dossier de demande d'autorisation (ou du PC)</b-th>
        <b-th v-if="isSubstantiel">Délivrance de l'avis de l'autorité environnementale</b-th>
        <b-th v-if="isSubstantiel">Arrêté d'autorisation ICPE</b-th>
        <b-th v-if="isSubstantiel">Déclaration d'ouverture du chantier de construction</b-th>
        <b-th v-if="isSubstantiel">Mise en service</b-th>
        <b-th v-if="isNotable">Arrêté préfectoral complémentaire</b-th>
        <b-th v-if="isEditable" class="table-action-column">Action(s)</b-th>
      </b-tr>
    </b-thead>
    <b-tbody>
      <b-tr v-for="(renouvellement) in renouvellements" :key="renouvellement.id">
        <b-td>
          <span v-if="renouvellement.type === 'S'">Substantiel</span>
          <span v-else>Notable</span>
        </b-td>
        <b-td v-if="isSubstantiel">
            <span v-if="renouvellement.dateDepotDemandeAutorisation">{{
                $filters.formatDate(renouvellement.dateDepotDemandeAutorisation)
              }}
            </span>
          <span v-else>{{ emptyField }}</span>
        </b-td>
        <b-td v-if="isSubstantiel">
            <span v-if="renouvellement.dateDelivranceAvisAutoriteEnvironnementale">{{
                $filters.formatDate(renouvellement.dateDelivranceAvisAutoriteEnvironnementale)
              }}</span>
          <span v-else>{{ emptyField }}</span>
        </b-td>
        <b-td v-if="isSubstantiel">
            <span
              v-if="renouvellement.dateDelivranceAutorisationIcpe"
            >{{
                $filters.formatDate(renouvellement.dateDelivranceAutorisationIcpe)
              }}</span
            >
          <span v-else>{{ emptyField }}</span>
        </b-td>
        <b-td v-if="isSubstantiel">
            <span v-if="renouvellement.dateDeclarationOuvertureChantierConstruction">
              {{ $filters.formatDate(renouvellement.dateDeclarationOuvertureChantierConstruction) }}
          </span>
          <span v-else>{{ emptyField }}</span>
        </b-td>
        <b-td v-if="isSubstantiel">
            <span v-if="renouvellement.dateMiseEnService">
              {{ $filters.formatDate(renouvellement.dateMiseEnService) }}
          </span>
          <span v-else>{{ emptyField }}</span>
        </b-td>
        <b-td v-if="isNotable">
            <span v-if="renouvellement.dateArretePrefectoralComplementaire">{{
                $filters.formatDate(renouvellement.dateArretePrefectoralComplementaire)
              }}</span>
          <span v-else>{{ emptyField }}</span>
        </b-td>
        <b-td v-if="isEditable" class="table-action-column">
          <b-button
            class="me-2"
            title="Supprimer le renouvellement"
            variant="danger"
            size="sm"
            outline
            icon
            @click="onClickRenouvellementDelete(renouvellement)"
            :disabled="isLock"
          >
            <i class="fa fa-trash"></i>
          </b-button>
          <b-button
            class="me-2"
            variant="success"
            size="sm"
            outline
            icon
            id="renouvellement-edit"
            @click="onClickRenouvellementEdit(renouvellement)"
            :disabled="isLock"
          >
            <b-tooltip target="#renouvellement-edit">
              Modifier le renouvellement
            </b-tooltip>
            <i class="fa fa-edit"></i>
          </b-button>
        </b-td>
      </b-tr>
    </b-tbody>
    <b-tfoot v-if="isEditable">
      <b-tr>
        <b-td></b-td>
        <b-td></b-td>
        <b-td></b-td>
        <b-td></b-td>
        <b-td></b-td>
        <b-td></b-td>
        <b-td></b-td>
        <b-td class="btn-cell">
          <b-button-group>
            <b-button
              variant="success"
              id="renouvellement-add"
              @click="showRenouvellementForm"
              :disabled="isLock"
            >
              Ajouter
              <b-tooltip target="#renouvellement-add">
                Renseigner un autre renouvellement
              </b-tooltip>
            </b-button>
          </b-button-group>
        </b-td>
      </b-tr>
    </b-tfoot>
  </b-table-simple>
</template>

<script setup>
import {
  BButton,
  BButtonGroup,
  BTableSimple,
  BTbody,
  BTd,
  BTfoot,
  BTh,
  BThead, BTooltip,
  BTr, useModal
} from "bootstrap-vue-next";
import store from "@/store";
import {useReactiveStoreGetters, useStoreGetters} from "@/composables/useStore";
import {computed} from "vue";

const {show: showRenouvellementDelete} = useModal('modal-renouvellement-delete');
const {show: showRenouvellementEdit} = useModal('modal-edit-renouvellement');
const {show: showRenouvellementForm} = useModal('modal-renouvellement-form');

// Props
const { renouvellements, prepareEdit } = defineProps([
  "renouvellements",
  "isLoading",
  "emptyField",
  "prepareEdit",
  "isEditable"
]);

// Getters
const { isLock } = useReactiveStoreGetters(["isLock"]);

// Computed
const hasRenouvellements = computed(() => {
  return renouvellements && renouvellements.length > 0;
});

// This code is correct because we display only one Renouvellement per table
const isSubstantiel = computed(() => {
  if (!hasRenouvellements.value) return false;
  return renouvellements.length > 1 || renouvellements[0].type === "S";
});

// This code is correct because we display only one Renouvellement per table
const isNotable = computed(() => {
  if (!hasRenouvellements.value) return false;
  return renouvellements.length > 1 || renouvellements[0].type === "N";
});

const selectRenouvellement = (renouvellement) => {
  store.dispatch("saveRenouvellement", renouvellement);
};

const onClickRenouvellementDelete = (renouvellement) => {
  selectRenouvellement(renouvellement);
  showRenouvellementDelete();
};

// Émissions
const emit = defineEmits(['edit-renouvellement']);

const onClickRenouvellementEdit = (renouvellement) => {
  emit('edit-renouvellement', renouvellement);
  showRenouvellementEdit();
};

</script>

<style scoped>
.table-action-column {
  text-align: right;
}
</style>
